import {useState} from 'react'
import {ArrowRight, Filter as FilterIcon, X} from 'lucide-react'
import {useI18n} from '@/i18n'
import {Badge} from '@/components/ui/badge'
import {Button} from '@/components/ui/button'
import {Skeleton} from '@/components/ui/skeleton'
import {ErrorBanner} from '@/components/ui/error-banner'
import {DatePicker} from '@/components/shared/DatePicker'
import {Stack} from '@/components/shared/Stack'
import {InlineRow} from '@/components/shared/InlineRow'
import {MutedText} from '@/components/shared/MutedText'
import {Icon} from '@/components/shared/Icon'
import {IconText} from '@/components/shared/IconText'
import {TextLink} from '@/components/shared/TextLink'
import {InlineEmptyText} from '@/components/shared/InlineEmptyText'
import {FormField} from '@/components/shared/FormField'
import {CheckboxOption} from '@/components/shared/CheckboxOption'
import {formatTimestamp, formatRelativeTime} from '@/lib/date-utils'
import {routes} from '@/lib/routes'
import type {IdentityLifecycleEvent, TriggerSource} from '@/types/identity'
import {IdentityLifecycleStateBadge, TriggerSource as TriggerSourceConst} from '@/types/identity'
import {TriggerSourceBadge} from '@/types/trigger-source-badge'

type IdentityLifecycleEventsFilters = {
    triggerSources: TriggerSource[]
    from: string
    to: string
}

const TRIGGER_REF_TYPE_JOB_EXECUTION = 'JOB_EXECUTION'
const TRIGGER_REF_TYPE_ACCESS_REQUEST = 'ACCESS_REQUEST_ITEM'
const ALL_TRIGGER_SOURCES = Object.values(TriggerSourceConst) as TriggerSource[]

/**
 * Vertical timeline of identity lifecycle events (spec §8.1.2). Newest first.
 *
 * <p>Each row shows the state-pair badge with arrow, trigger-source badge,
 * actor pill, relative timestamp (absolute on hover), italic reason block when
 * present, and a typed "trigger-ref" deep-link when {@code triggerRefType} /
 * {@code triggerRefId} are populated.
 *
 * <p>Filters (collapsed by default) — trigger-source multi-select + date range.
 * Empty / loading / error states each have their own visual.
 */
export interface IdentityLifecycleTimelineProps {
    events: IdentityLifecycleEvent[]
    isLoading: boolean
    isError: boolean
    errorObj?: unknown
    filters: IdentityLifecycleEventsFilters
    onFiltersChange: (next: IdentityLifecycleEventsFilters) => void
    'data-testid'?: string
}

export function IdentityLifecycleTimeline({
    events,
    isLoading,
    isError,
    errorObj,
    filters,
    onFiltersChange,
    'data-testid': testid,
}: IdentityLifecycleTimelineProps) {
    const i18n = useI18n()
    const [filtersOpen, setFiltersOpen] = useState(false)

    const hasActiveFilters = filters.triggerSources.length > 0 || filters.from !== '' || filters.to !== ''

    const clearFilters = () => onFiltersChange({triggerSources: [], from: '', to: ''})

    const toggleTriggerSource = (source: TriggerSource) => {
        const next = filters.triggerSources.includes(source)
            ? filters.triggerSources.filter((s) => s !== source)
            : [...filters.triggerSources, source]
        onFiltersChange({...filters, triggerSources: next})
    }

    return (
        <Stack gap="md" data-testid={testid ?? 'lifecycle-timeline'}>
            <InlineRow gap="sm" justify="end">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setFiltersOpen((open) => !open)}
                    data-testid="btn-toggle-lifecycle-filters"
                >
                    <IconText icon={FilterIcon} size="sm">
                        {i18n.lifecycleEvents.filter.toggleFilters}
                    </IconText>
                </Button>
                {hasActiveFilters && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearFilters}
                        data-testid="btn-clear-lifecycle-filters"
                    >
                        <IconText icon={X} size="sm">
                            {i18n.lifecycleEvents.filter.clearFilters}
                        </IconText>
                    </Button>
                )}
            </InlineRow>

            {filtersOpen && (
                <Stack gap="sm" data-testid="lifecycle-filters-panel">
                    <Stack gap="xs">
                        <span className="text-sm font-medium">{i18n.lifecycleEvents.filter.triggerSource}</span>
                        {ALL_TRIGGER_SOURCES.map((source) => (
                            <CheckboxOption
                                key={source}
                                checked={filters.triggerSources.includes(source)}
                                onToggle={() => toggleTriggerSource(source)}
                            >
                                {i18n.lifecycleEvents.triggerSource[source]}
                            </CheckboxOption>
                        ))}
                    </Stack>
                    <InlineRow gap="sm">
                        <FormField label={String(i18n.lifecycleEvents.filter.from)} htmlFor="lifecycle-filter-from">
                            <DatePicker
                                testId="input-lifecycle-filter-from"
                                withTime
                                value={filters.from}
                                onChange={(iso) => onFiltersChange({...filters, from: iso})}
                                ariaLabel={String(i18n.lifecycleEvents.filter.from)}
                            />
                        </FormField>
                        <FormField label={String(i18n.lifecycleEvents.filter.to)} htmlFor="lifecycle-filter-to">
                            <DatePicker
                                testId="input-lifecycle-filter-to"
                                withTime
                                value={filters.to}
                                onChange={(iso) => onFiltersChange({...filters, to: iso})}
                                ariaLabel={String(i18n.lifecycleEvents.filter.to)}
                            />
                        </FormField>
                    </InlineRow>
                </Stack>
            )}

            {isLoading && (
                <Stack gap="sm" data-testid="lifecycle-timeline-loading">
                    <Skeleton className="h-12 w-full"/>
                    <Skeleton className="h-12 w-full"/>
                    <Skeleton className="h-12 w-full"/>
                </Stack>
            )}

            {isError && !isLoading && (
                <div data-testid="lifecycle-timeline-error">
                    <ErrorBanner error={(errorObj as Error | null) ?? null}/>
                </div>
            )}

            {!isLoading && !isError && events.length === 0 && (
                <InlineEmptyText data-testid="lifecycle-timeline-empty">
                    {hasActiveFilters
                        ? i18n.lifecycleEvents.empty.noResultsForFilters
                        : i18n.lifecycleEvents.empty.noEvents}
                </InlineEmptyText>
            )}

            {!isLoading && !isError && events.length > 0 && (
                <Stack gap="sm" data-testid="lifecycle-timeline-rows">
                    {events.map((event) => (
                        <TimelineRow key={event.id} event={event}/>
                    ))}
                </Stack>
            )}
        </Stack>
    )
}

function TimelineRow({event}: {event: IdentityLifecycleEvent}) {
    const i18n = useI18n()
    const toBadge = IdentityLifecycleStateBadge.of(event.toState)
    const fromBadge = event.fromState ? IdentityLifecycleStateBadge.of(event.fromState) : null
    const triggerBadge = TriggerSourceBadge.of(event.triggerSource)
    const triggerLabel = i18n.lifecycleEvents.triggerSource[event.triggerSource]
    const actorName = event.actor?.displayName ?? i18n.lifecycleEvents.timeline.system
    const absoluteTimestamp = formatTimestamp(event.changedAt)
    const relativeTimestamp = formatRelativeTime(event.changedAt)

    return (
        <Stack
            gap="xs"
            className="rounded-md border bg-muted/30 p-3"
            data-testid={`lifecycle-event-${event.id}`}
        >
            <InlineRow gap="sm" wrap>
                {fromBadge ? (
                    <>
                        <Badge variant={fromBadge.variant}>{event.fromState}</Badge>
                        <Icon icon={ArrowRight} size="xs" muted/>
                        <Badge variant={toBadge?.variant}>{event.toState}</Badge>
                    </>
                ) : (
                    <Badge variant={toBadge?.variant}>
                        {i18n.lifecycleEvents.timeline.createdLabel}
                        {' '}
                        {event.toState}
                    </Badge>
                )}
                <Badge variant={triggerBadge.variant} data-testid={`lifecycle-event-${event.id}-trigger`}>
                    {triggerLabel}
                </Badge>
                <MutedText size="sm" className="ml-auto">
                    <span title={absoluteTimestamp}>{relativeTimestamp}</span>
                </MutedText>
            </InlineRow>
            <MutedText size="sm">
                <span data-testid={`lifecycle-event-${event.id}-actor`}>{actorName}</span>
            </MutedText>
            {event.reason && (
                <p className="italic text-sm" data-testid={`lifecycle-event-${event.id}-reason`}>
                    {event.reason}
                </p>
            )}
            {event.triggerRefType && event.triggerRefId && (
                <TriggerRefLink type={event.triggerRefType} id={event.triggerRefId}/>
            )}
        </Stack>
    )
}

function TriggerRefLink({type, id}: {type: string; id: string}) {
    const i18n = useI18n()
    if (type === TRIGGER_REF_TYPE_JOB_EXECUTION) {
        return (
            <TextLink
                to={routes.backgroundJobs.executions.detail(id)}
                stopPropagation
                testId="lifecycle-event-trigger-ref-link"
            >
                {i18n.lifecycleEvents.timeline.viewJobExecution}
            </TextLink>
        )
    }
    if (type === TRIGGER_REF_TYPE_ACCESS_REQUEST) {
        return (
            <TextLink
                to={routes.accessGovernance.accessRequests.detail(id)}
                stopPropagation
                testId="lifecycle-event-trigger-ref-link"
            >
                {i18n.lifecycleEvents.timeline.viewAccessRequest}
            </TextLink>
        )
    }
    return null
}
